import { proxy } from "@web-monorepo/hooks/src/valtioReexport";
import { hidePriorityModal, modalStore, showPriorityModal } from "#/src/pages/_components/modals/modalStore";
import {
  MarketplaceSessionAvailablePayload,
  MarketplaceSessionClaimedPayload,
} from "@web-monorepo/api-types/zodSchemas/realtime/marketplace";
import { showNotification } from "#/src/utils/notification";
import { logTutorEvent } from "#/src/utils/log";
import { startHeartbeat } from "#/src/util/natsHeartbeat";
import * as realtime from "#/src/util/realtime";

const CONNECTION_DURATION_MS = 1000 * 60 * 60 * 4;

// Re-queueing delay after a user rejects an SNTS offer. Lets the modal fully
// dismiss + animate out before we show the next one (avoids a flicker if there
// are several offers stacked up).
const DEQUEUE_DELAY_MS = 100;

export type TutorRealtimeStatus = { _tag: "disconnected" } | { _tag: "connected"; activeUntil: Date };

// --- Shared message handlers (exported so the NATS subscription hook can call them) ---
export function handleSessionAvailable(payload: MarketplaceSessionAvailablePayload) {
  if (tutorRealtimeStore.getDismissedSessions().includes(payload.sessionId)) return;

  tutorRealtimeStore.pendingSntsOffers.push({
    sessionId: payload.sessionId,
    timestamp: payload.timestamp,
    offerId: payload.offerId,
  });
  if (modalStore.priorityModal == null) {
    dequeue();
  }
}

export function handleSessionClaimed(payload: MarketplaceSessionClaimedPayload) {
  const idx = tutorRealtimeStore.pendingSntsOffers.findIndex((offer) => offer.sessionId === payload.sessionId);
  if (idx === -1) return;

  tutorRealtimeStore.pendingSntsOffers.splice(idx, 1);
  hidePriorityModal();
  showNotification({
    title: "Last minute session claimed",
    message: "The last minute session has been claimed by another tutor",
  });
}

function dequeue() {
  const offer = tutorRealtimeStore.pendingSntsOffers.shift();
  if (!offer) return;

  showPriorityModal({
    type: "lastMinuteSessionOfferModal",
    offer,
    onTimeout: () => {
      hidePriorityModal();
      tutorRealtimeStore.disconnect();
    },
    onReject: () => {
      tutorRealtimeStore.addDismissedSession(offer.sessionId);
      setTimeout(dequeue, DEQUEUE_DELAY_MS);
      hidePriorityModal();
    },
  });
}

export const tutorRealtimeStore = proxy({
  pendingSntsOffers: [] as MarketplaceSessionAvailablePayload[],
  dismissedSessions: [] as string[],
  status: { _tag: "disconnected" } as TutorRealtimeStatus,
  timeout: null as ReturnType<typeof setTimeout> | null,
  params: null as {
    tutorId: string;
  } | null,
  natsHeartbeatCleanup: null as (() => void) | null,
  setParams: (params: { tutorId: string }) => {
    tutorRealtimeStore.params = { ...tutorRealtimeStore.params, ...params };
  },
  addDismissedSession: (sessionId: string) => {
    tutorRealtimeStore.dismissedSessions.push(sessionId);
  },
  getDismissedSessions: () => {
    return tutorRealtimeStore.dismissedSessions;
  },
  disconnect: () => {
    // SNTS-level teardown only: stop the heartbeat, clear offers, reset status.
    // The subscription tears down automatically when the provider observes
    // `status === disconnected` and flips `enabled` to false. The shared NATS
    // connection is deliberately NOT closed here — it may still serve other
    // subscribers (messaging), and closing it wouldn't resubscribe them.
    // Connection lifecycle is owned by logout (`useLogoutMutation`), the one
    // moment the connection's auth is known to be dead for every consumer.
    if (tutorRealtimeStore.natsHeartbeatCleanup) {
      tutorRealtimeStore.natsHeartbeatCleanup();
      tutorRealtimeStore.natsHeartbeatCleanup = null;
    }

    if (tutorRealtimeStore.timeout) {
      clearTimeout(tutorRealtimeStore.timeout);
      tutorRealtimeStore.timeout = null;
    }
    tutorRealtimeStore.pendingSntsOffers.splice(0);
    tutorRealtimeStore.status = { _tag: "disconnected" };
  },
  connect: () => {
    if (tutorRealtimeStore.params == null) return;

    const tutorId = tutorRealtimeStore.params.tutorId;
    tutorRealtimeStore.timeout = setTimeout(() => tutorRealtimeStore.disconnect(), CONNECTION_DURATION_MS);
    tutorRealtimeStore.status = { _tag: "connected", activeUntil: new Date(Date.now() + CONNECTION_DURATION_MS) };

    // Start the NATS presence heartbeat. The `useMarketplaceTutorRealtime` hook
    // (rendered by the provider) owns the message subscription; both share the
    // same RealtimeClient via `createRealtimeHook`, and calling `publish` here
    // lazy-inits it, opening a `wss://{host}/nats` connection.
    tutorRealtimeStore.natsHeartbeatCleanup = startHeartbeat(realtime.publish, tutorId);
    logTutorEvent({ eventName: "marketplace.nats.connection", metadata: { userId: tutorId } });
  },
});
